import { Flicking as CustomFlicking } from '#atoms';
import { Modal } from '#molecules';
import '@egjs/react-flicking/dist/flicking.css';
import { useState } from 'react';
import styled from 'styled-components';
import { Button } from '../Button';
import { Flex } from '../Flex';

type TimePickerInputProps = {
  date?: Date;
  onChange?: (date: Date) => void;
  label?: string;
  title?: string;
  style?: React.CSSProperties;
  fullWidth?: boolean;
  placeholder?: string;
  minuteStep?: number;
};

type Payload = {
  hour: number;
  minute: number;
};

export function TimePickerInput({
  label,
  date,
  onChange,
  title,
  style,
  fullWidth,
  placeholder,
  minuteStep = 5
}: TimePickerInputProps) {
  const selections = {
    hour: new Array(24).fill(0).map((_, i) => i),
    minute: new Array(60 / minuteStep).fill(0).map((_, i) => i * minuteStep)
  };

  const [isOpen, setIsOpen] = useState(false);
  const [payload, setPayload] = useState<Payload>({
    hour: date ? date.getHours() : 0,
    minute: date ? selections.minute.find(m => m === date.getMinutes()) ?? 0 : 0
  });

  const formatted = date
    ? `${String(date.getHours()).padStart(2, '0')}:${String(
        selections.minute.find(m => m === date.getMinutes()) ?? 0
      ).padStart(2, '0')}`
    : '';

  const closeModal = () => {
    setIsOpen(false);
  };

  const handlePayloadChange = (key: keyof Payload, value: string) => {
    setPayload(payload => ({ ...payload, [key]: Number(value) }));
  };

  const handleOk = async () => {
    await new Promise(resolve => setTimeout(resolve, 200));
    const newDate = new Date(1970, 1, 1, payload.hour, payload.minute);
    if (onChange) onChange(newDate);
    closeModal();
  };

  return (
    <>
      <Wrapper style={{ width: fullWidth ? '100%' : 'auto', ...style }}>
        {label && <Label>{label}</Label>}
        <Input onClick={() => setIsOpen(true)}>
          {Boolean(date) ? (
            formatted
          ) : (
            <Placeholder>{placeholder ?? '00:00'}</Placeholder>
          )}
        </Input>
      </Wrapper>
      {isOpen && (
        <Modal onClose={closeModal} style={{ maxWidth: '320px' }}>
          <ModalWrapper direction="column" fullWidth>
            <ModalTitle>{title}</ModalTitle>
            <FlickingWrapper>
              <CustomFlicking
                list={selections.hour.map(String)}
                defaultIndex={selections.hour.findIndex(
                  h => h === date?.getHours()
                )}
                bgColor="transparent"
                borderColor="transparent"
                wrapperProps={{ style: { flex: 1 } }}
                onChange={handlePayloadChange.bind(null, 'hour')}
              />
              <CustomFlicking
                list={selections.minute.map(v => String(v).padStart(2, '0'))}
                defaultIndex={selections.minute.findIndex(
                  m => m === date?.getMinutes()
                )}
                bgColor="transparent"
                borderColor="transparent"
                wrapperProps={{ style: { flex: 1 } }}
                onChange={handlePayloadChange.bind(null, 'minute')}
              />
            </FlickingWrapper>
            <Flex gap={12}>
              <Button size="sm" background="anti" onClick={closeModal}>
                닫기
              </Button>
              <Button size="sm" onClick={handleOk}>
                확인
              </Button>
            </Flex>
          </ModalWrapper>
        </Modal>
      )}
    </>
  );
}

const Wrapper = styled.div``;

const Label = styled.div`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.gray25};
  margin-bottom: 8px;
`;

const Input = styled.div`
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.palette.gray400};
  padding: 10px 16px;
  color: ${({ theme }) => theme.palette.gray15};
`;

const ModalWrapper = styled(Flex)``;

const FlickingWrapper = styled.div`
  margin: 1rem 0;
  display: flex;
`;

const ModalTitle = styled.div`
  color: ${({ theme }) => theme.palette.gray15};
  font-size: 20px;
  font-weight: 600;
  text-align: left;
  margin-left: 4px;
`;

const Placeholder = styled.span`
  color: ${({ theme }) => theme.palette.gray300};
`;
